import { useCtraderOAuthCallbackMutation } from '@/service/ctrader/ctrader.hook';
import { createFileRoute, useSearch } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ImSpinner8 } from 'react-icons/im';
import z from 'zod';

const STORAGE_KEY = 'ctrader_pending_state';

const callbackSchema = z.object({
  code: z.string().optional(),
  state: z.string().optional(),
  ctUrl: z.string().optional(),
});

export const Route = createFileRoute('/ctrader-callback')({
  component: RouteComponent,
  validateSearch: (params) => callbackSchema.parse(params),
});

function RouteComponent() {
  const { t } = useTranslation();
  const params = useSearch({ from: '/ctrader-callback' });
  const [done, setDone] = useState(false);
  const [error, setError] = useState(false);
  const called = useRef(false);

  const callbackMutation = useCtraderOAuthCallbackMutation({
    onSuccess() {
      localStorage.removeItem(STORAGE_KEY);
      setDone(true);
    },
    onError() {
      localStorage.removeItem(STORAGE_KEY);
      setError(true);
    },
  });

  useEffect(() => {
    // Phase 1 : init — stocker le state et rediriger vers cTrader
    if (params.state && params.ctUrl && !params.code) {
      localStorage.setItem(STORAGE_KEY, params.state);
      window.location.href = params.ctUrl;
      return;
    }

    // Phase 2 : callback depuis cTrader
    if (params.code && !called.current) {
      const state = localStorage.getItem(STORAGE_KEY);
      if (!state) {
        setError(true);
        return;
      }
      called.current = true;
      callbackMutation.mutate({ code: params.code, state });
    }
  }, []);

  if (error) {
    return (
      <div className="min-h-dvh flex items-center justify-center flex-col gap-4">
        <p className="text-red-400 text-lg">{t('ctrader.error')}</p>
        <p className="text-neutral-400 text-sm">{t('ctrader.errorHint')}</p>
      </div>
    );
  }

  if (done) {
    return (
      <div className="min-h-dvh flex items-center justify-center flex-col gap-4">
        <p className="text-green-400 text-lg">{t('ctrader.success')}</p>
        <p className="text-neutral-400 text-sm">{t('ctrader.successHint')}</p>
      </div>
    );
  }

  return (
    <div className="min-h-dvh flex items-center justify-center flex-col gap-4">
      <p className="text-lg">{t('ctrader.connecting')}</p>
      <ImSpinner8 className="animate-spin size-8" />
    </div>
  );
}
